import clsx from "clsx";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useMemo, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import {
  getDrawRunWinners,
  useDrawRunWinners,
  useFutureRaffleDraws,
  useIsWinnersLoading,
  useRaffleDrawsHistory,
  useRaffleDrawsHistoryLoading,
  useRaffleRunPagination,
} from "../../store";
import { useIsMobile } from "../../../device/selectors";
import { SmarticoRaffleDrawIds } from "../../interfaces/SmarticoRaffleDrawIds";
import { useAppDispatch } from "../../../../hooks";
import useSmartico from "../../../../../hooks/useSmartico";
import { useRaffleContext } from "@/app/(custom-header)/spring-daily-raffle/context";

dayjs.extend(relativeTime);

const RaffleTableRender = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const api = useSmartico();
  const { isEndState } = useRaffleContext();
  const allDraws = useRaffleDrawsHistory();
  const areDrawsLoading = useRaffleDrawsHistoryLoading();
  const futureDraws = useFutureRaffleDraws();
  const containerRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const [page, setPage] = useState(1);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const activeRunId = allDraws?.[activeSlideIndex]?.run_id;
  const activeDrawRunId = activeSlideIndex !== -1 ? activeRunId || null : null;
  const winners = useDrawRunWinners(activeDrawRunId);
  const isWinnersLoading = useIsWinnersLoading(activeDrawRunId);
  const numberFormatter = new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  const { winners_total, winners_limit, winners_offset } = useRaffleRunPagination(activeRunId);

  const currentActiveSlideIndex = allDraws.findLastIndex(draw => Date.now() >= draw.execution_ts);

  const totalPages = Math.ceil(winners_total / winners_limit);

  const shownPages = useMemo(() => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (page <= 4) {
      for (let i = 1; i <= Math.min(page + 1, 5); i++) {
        pages.push(i);
      }
      pages.push("...", totalPages - 1, totalPages);
    } else if (page > totalPages - 4) {
      pages.push(1, 2, "...");
      const startPage = Math.max(totalPages - (totalPages - page + 1), totalPages - 4);
      for (let i = startPage; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1, "...", page - 1, page, page + 1, "...", totalPages - 1, totalPages);
    }

    return pages;
  }, [page, totalPages]);

  const goToPage = (newPage: number | string) => {
    if (typeof newPage === "number" && newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      dispatch(
        getDrawRunWinners({
          api,
          run_id: activeRunId,
          from: winners_limit * (newPage - 1),
          to: winners_limit * newPage,
        })
      );
    }
  };

  const givenPrizes = useMemo(() => {
    const dailyHistoryDraws = allDraws.filter(draw => draw.id === SmarticoRaffleDrawIds.HOURLY_DRAW);
    return dailyHistoryDraws.length * 6000;
  }, [allDraws]);

  if (!areDrawsLoading && allDraws.length === 0) {
    return null;
  }

  return (
    // full width container
    <div ref={containerRef} className={"bg-gray-4 pb-6 lg:!py-16 font-jost"}>
      {/*centered content*/}
      <div
        className={`mx-auto relative overflow-hidden bg-gray-6 lg:max-w-[916px] lg:rounded-3xl ${isEndState ? "lg:shadow-[0_1.5px_8px_0_rgba(0,0,0,0.20)]" : ""}`}>
        {/*TITLE*/}
        {isEndState ? (
          <div className={"mb-4 text-center uppercase text-lg leading-6 font-bold pt-8 lg:text-2xl lg:leading-7.5"}>
            And the Winners Are…🥁
          </div>
        ) : givenPrizes === 0 ? null : (
          <div className={"mb-4 text-center uppercase text-lg leading-6 font-bold pt-8 lg:text-2xl lg:leading-7.5"}>
            {numberFormatter.format(givenPrizes)} prizes already won
          </div>
        )}

        {/*calendar swiper*/}
        {(!areDrawsLoading || allDraws.length > 0) && (currentActiveSlideIndex !== -1 || futureDraws.length > 0) ? (
          <div className={"mx-3 pb-6 relative"}>
            {!isMobile && (
              <div className={"absolute -left-3 top-0 z-30 h-12 w-16 bg-gradient-to-l from-gray-6/0 to-gray-6"}></div>
            )}
            <Swiper
              onSwiper={swiper => {
                swiperRef.current = swiper;
                dispatch(
                  getDrawRunWinners({
                    api,
                    run_id: allDraws[swiper.realIndex].run_id,
                  })
                );
              }}
              initialSlide={currentActiveSlideIndex === -1 ? 0 : currentActiveSlideIndex}
              onSlideChange={swiper => {
                const isPast = !!allDraws[swiper.realIndex];
                if (isPast && activeSlideIndex !== swiper.realIndex) {
                  setActiveSlideIndex(swiper.realIndex);
                  dispatch(
                    getDrawRunWinners({
                      api,
                      run_id: allDraws[swiper.realIndex].run_id,
                    })
                  );
                  setPage(1);
                }
              }}
              centeredSlides={true}
              slidesPerView={isMobile ? 7 : 19}
              spaceBetween={16}
              className={"big-7-draws-slides-swiper !overflow-visible"}>
              {[...allDraws, ...futureDraws].map((slide, index) => {
                const isActive = activeSlideIndex === index;
                const isPast = allDraws.findIndex(draw => draw.run_id === slide.run_id) !== -1;
                const isFuture = futureDraws.findIndex(draw => draw.run_id === slide.run_id) !== -1;

                return (
                  <SwiperSlide key={`${slide.ticket_start_ts}-${index}`}>
                    <button
                      onClick={() => {
                        if (isPast) {
                          setActiveSlideIndex(index);
                          swiperRef.current?.slideTo(index);
                        }
                      }}
                      disabled={isFuture}
                      className={clsx("size-12 transition-all whitespace-nowrap", {
                        ["bg-primary text-white rounded"]: isActive && isPast,
                        ["!text-[#BCBCC] cursor-not-allowed"]: isFuture,
                      })}>
                      <div className={`text-[10px] ${isActive && isPast ? "text-white" : "text-gray-5"}`}>
                        {dayjs(slide.execution_ts).format("ddd").toUpperCase()}
                      </div>
                      <div
                        className={`text-sm font-bold leading-5 ${isActive && isPast ? "text-white" : "text-gray-1"}`}>
                        {dayjs(slide.execution_ts).format("D").toUpperCase()}
                      </div>
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            {!isMobile && (
              <div className={"absolute -right-3 top-0 z-30 h-12 w-16 bg-gradient-to-l from-gray-6 to-gray-6/0"}></div>
            )}
          </div>
        ) : null}

        {/*TABLE*/}
        <div className={"px-3 mx-auto lg:max-w-[716px]"}>
          {/*table header*/}
          <div
            className={
              "flex items-center bg-[#E7E7E9] text-gray-1 text-[11px] lg:text-sm font-medium leading-3.5 lg:leading-5"
            }>
            <div className={"min-w-[92px] lg:min-w-[220px] px-2 lg:pl-16 lg:pr-0 uppercase py-2"}>Winner</div>
            <div className={"px-2 lg:pl-16 lg:pr-0 uppercase py-2"}>Prize</div>
          </div>

          {/*table data*/}
          <div className={" overflow-y-auto mb-4 lg:mb-10 min-h-[384px] lg:min-h-[448px]"}>
            {areDrawsLoading || isWinnersLoading ? (
              <div className={"px-8 py-8 text-center text-black"}>
                <div>Loading...</div>
              </div>
            ) : (
              <>
                {/* Winners */}
                {winners.length > 0 && (
                  <>
                    {winners.map((winner, idx) => (
                      <div key={`${winner.id}-${idx}`} className={"border-b last:border-b-0 border-black/8 py-1"}>
                        <div className={"flex items-center justify-between"}>
                          <div
                            className={
                              "min-w-[92px] lg:min-w-[220px] text-sm flex gap-2 lg:gap-3 px-2 lg:pl-16 lg:pr-0 h-11 justify-start items-center"
                            }>
                            {winner.avatar_url && (
                              <div className="size-7 lg:size-10">
                                <img
                                  src={winner.avatar_url}
                                  alt={`avatar-${idx}`}
                                  width={24}
                                  height={24}
                                  className="h-full w-auto"
                                />
                              </div>
                            )}
                            <span className="text-[13px] lg:text-base text-secondary font-medium leading-4.5 lg:leading-5.5">
                              {winner.username || winner.id}
                            </span>
                          </div>
                          <div className={"text-sm flex-1 flex gap-2 px-2 lg:pl-16 lg:pr-0 lg:gap-3 items-center"}>
                            <img
                              height={36}
                              width={54}
                              src={winner.prize.image_url}
                              alt="prize-image"
                              className={"h-9 lg:h-11 w-auto"}
                            />
                            <span
                              className={
                                "text-[13px] lg:text-base text-green-2 font-semibold leading-4.5 lg:leading-5.5"
                              }>
                              {winner.prize.name || "Prize"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* No winners */}
                {winners_total === 0 && !isEndState && (
                  <div className={"px-8 py-8 text-center text-gray-1"}>
                    <div>No winners yet</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/*PAGINATION*/}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 lg:gap-4 max-w-[400px] mx-auto pb-8">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className={clsx(
                "p-1 rounded-lg size-8 lg:size-10 transition-opacity",
                page === 1 ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              )}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 4L6 8L10 12"
                  stroke="black"
                  stroke-opacity="0.38"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {/*<ChevronDown className="transform rotate-90 text-[#8F8F8F] size-full" />*/}
            </button>
            {shownPages.map((_page, idx) => (
              <button
                key={`${_page}-${idx}`}
                onClick={() => _page !== "..." && goToPage(_page as number)}
                disabled={_page === "..."}
                className={clsx(
                  "flex font-normal justify-center border border-[#E0E0E0] items-center size-8 rounded lg:text-[15px] text-secondary",
                  {
                    "!text-white bg-primary": _page === page,
                    "cursor-pointer": _page !== "...",
                    "cursor-default border-none text-black !opacity-100": _page === "...",
                  }
                )}>
                {_page}
              </button>
            ))}
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
              className={clsx(
                "p-1 rounded-lg size-8 lg:size-10 transition-opacity",
                page === totalPages ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
              )}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 12L10 8L6 4"
                  stroke="#4C4C52"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {/*<ChevronDown className="transform -rotate-90 text-[#8F8F8F] size-full" />*/}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaffleTableRender;
